import React, { useState, useEffect, useRef } from 'react';
import { Languages, Send, Paperclip, X, Loader, Trash2 } from 'lucide-react';
import { subscribeToChat, sendMessage, markMessagesAsRead, deleteMessage } from '../../services/chatService';
import { uploadChatFile, validateFile, getFileIcon, formatFileSize, deleteFile } from '../../services/fileService';
import { getActiveProject } from '../../services/projectService';
import { ref, getBlob } from 'firebase/storage';
import { storage } from '../../firebase';
import ImageModal from './ImageModal';
import './ChatWindow.css';

const truncateFileName = (name, max = 25) => {
  if (!name) return '';
  const ext = name.includes('.') ? name.slice(name.lastIndexOf('.')) : '';
  const base = name.slice(0, name.length - ext.length);
  if (base.length + ext.length <= max) return name;
  return base.slice(0, max - ext.length - 3) + '...' + ext;
};



// Loads image via Firebase SDK if direct URL fails (auth-protected storage)
const ChatImage = ({ src, alt, storagePath, className, onClick }) => {
  const [imgSrc, setImgSrc] = React.useState(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let revoke = null;
    const load = async () => {
      // Always load via Firebase SDK to bypass auth on img tag
      if (storagePath) {
        try {
          const fileRef = ref(storage, storagePath);
          const blob = await getBlob(fileRef);
          const blobUrl = URL.createObjectURL(blob);
          revoke = blobUrl;
          setImgSrc(blobUrl);
          return;
        } catch (e) {
          // fall through to direct URL
        }
      }
      // Fallback: try direct URL
      if (src) setImgSrc(src);
      else setFailed(true);
    };
    load();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [src, storagePath]);

  if (failed) return <div className="message-image-error">{alt} — Image not available</div>;
  if (!imgSrc) return <div className="message-image-loading">Loading...</div>;
  return <img src={imgSrc} alt={alt} className={className} onClick={onClick} onError={() => setFailed(true)} />;
};

const ChatWindow = ({ chatId, currentUser, recipientName }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [deletingMessageId, setDeletingMessageId] = useState(null);
  const [activeProject, setActiveProject] = useState(null);
  const isAdminUser = currentUser?.email === process.env.REACT_APP_ADMIN_EMAIL;
  const [targetLang, setTargetLang] = useState('es');
  const [sourceLang, setSourceLang] = useState('en');
  const [translations, setTranslations] = useState({}); // { msgId: translatedText }
  const [translatingId, setTranslatingId] = useState(null);
  const [translatingInput, setTranslatingInput] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Subscribe to messages
  useEffect(() => {
    if (!chatId) return;

    setLoading(true);
    let isFirstLoad = true;
    const unsubscribe = subscribeToChat(chatId, (msgs) => {
      setMessages(msgs);
      setLoading(false);
      
      // Mark messages as read
      if (currentUser?.uid) {
        markMessagesAsRead(chatId, currentUser.uid);
      }

      // Scroll to bottom instantly on first load
      if (isFirstLoad) {
        isFirstLoad = false;
        setTimeout(() => scrollToBottom('instant'), 50);
      }
    });

    return () => unsubscribe();
  }, [chatId, currentUser]);

  // Load active project for the client
  useEffect(() => {
    const loadActiveProject = async () => {
      try {
        const project = await getActiveProject(chatId); // chatId = clientId
        setActiveProject(project);
      } catch (error) {
        console.error('Error loading active project:', error);
      }
    };

    if (chatId) {
      loadActiveProject();
    }
  }, [chatId]);

  // Auto-scroll to bottom only if user is already near the bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const threshold = 120; // px from bottom
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    if (isNearBottom) {
      scrollToBottom();
    }
  }, [messages]);

  // Close modal on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && selectedImage) {
        setSelectedImage(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [selectedImage]);

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const LANGUAGES = [
    { code: 'es', label: 'Spanish' },
    { code: 'en', label: 'English' },
    { code: 'fr', label: 'French' },
    { code: 'pt', label: 'Portuguese' },
    { code: 'de', label: 'German' },
    { code: 'it', label: 'Italian' },
  ];

  const translateText = async (text, to, from) => {
    const res = await fetch(
      `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${from}|${to}`
    );
    const data = await res.json();
    if (data.responseStatus === 200) return data.responseData.translatedText;
    throw new Error(data.responseDetails || 'Translation failed');
  };

  const handleTranslateMessage = async (msgId, text) => {
    if (translations[msgId]) {
      // Toggle off
      setTranslations(prev => { const n = {...prev}; delete n[msgId]; return n; });
      return;
    }
    setTranslatingId(msgId);
    try {
      const translated = await translateText(text, targetLang, sourceLang);
      setTranslations(prev => ({ ...prev, [msgId]: translated }));
    } catch (e) {
      alert('Translation error: ' + e.message);
    } finally {
      setTranslatingId(null);
    }
  };

  const handleTranslateInput = async () => {
    if (!newMessage.trim()) return;
    setTranslatingInput(true);
    try {
      const translated = await translateText(newMessage.trim(), targetLang, sourceLang);
      setNewMessage(translated);
    } catch (e) {
      alert('Translation error: ' + e.message);
    } finally {
      setTranslatingInput(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    try {
      let fileData = null;

      // Upload file if selected
      if (selectedFile) {
        setUploading(true);
        fileData = await uploadChatFile(
          selectedFile,
          chatId,
          (progress) => setUploadProgress(progress)
        );
        setUploading(false);
      }

      // Determine user role
      const adminEmail = process.env.REACT_APP_ADMIN_EMAIL;
      const userRole = currentUser.email === adminEmail ? 'admin' : 'client';

      // Send message
     const clientInfo = {
  clientId: chatId,
  clientName: recipientName,
  projectId: activeProject?.id || null,
  projectName: activeProject?.name || null,
  projectStatus: activeProject?.status || null
};
      await sendMessage(
        chatId,
        currentUser.uid,
        currentUser.displayName || currentUser.email,
        userRole,
        newMessage.trim(),
        fileData,
        clientInfo
      );

      // Clear input
      setNewMessage('');
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error al enviar el mensaje');
      setUploading(false);
    }
  };

  const handleDeleteMessage = async (messageId, fileURL) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este mensaje?')) {
      return;
    }

    try {
      setDeletingMessageId(messageId);

      // Delete file from Storage if exists
      if (fileURL) {
        await deleteFile(fileURL);
      }

      // Delete message from Firestore
      await deleteMessage(messageId);

      setDeletingMessageId(null);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Error al eliminar el mensaje');
      setDeletingMessageId(null);
    }
  };

  const canDeleteMessage = (message) => {
    // Only sender can delete
    if (message.senderId !== currentUser.uid) return false;

    // Check if within 10 minutes for text messages
    if (!message.fileURL) {
      const messageTime = message.createdAt?.toDate();
      const now = new Date();
      const diffMinutes = (now - messageTime) / 1000 / 60;
      return diffMinutes <= 10;
    }

    // Images can always be deleted
    return true;
  };

  const handleFileDownload = async (url, fileName, storagePath) => {
    try {
      if (storagePath) {
        const fileRef = ref(storage, storagePath);
        const blob = await getBlob(fileRef);
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      window.open(url, '_blank');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    setSelectedFile(file);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleImageClick = (imageUrl, fileName, storagePath) => {
    setSelectedImage({ url: imageUrl, name: fileName, storagePath });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 minute
    if (diff < 60000) return 'Ahora';
    
    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return `Hace ${minutes} min`;
    }
    
    // Less than 24 hours
    if (diff < 86400000) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    }
    
    // More than 24 hours
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  const isImage = (fileType) => {
    return fileType && fileType.startsWith('image/');
  };

  if (loading) {
    return (
      <div className="chat-window-loading">
        <Loader className="spinner" />
        <p>Cargando chat...</p>
      </div>
    );
  }

  return (
    <>
      <div className="chat-window">
        {/* Header */}
        <div className="chat-header">
          <div>
            {isAdminUser && <h3>{recipientName}</h3>}
          </div>
          {isAdminUser && (
            <div className="chat-translate-lang">
              <Languages size={14} />
              <select
                value={sourceLang}
                onChange={e => { setSourceLang(e.target.value); setTranslations({}); }}
                className="lang-select"
              >
                {LANGUAGES.map(l => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
              <span style={{fontSize:11,color:'#9ca3af'}}>→</span>
              <select
                value={targetLang}
                onChange={e => { setTargetLang(e.target.value); setTranslations({}); }}
                className="lang-select"
              >
                {LANGUAGES.map(l => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="chat-messages" ref={messagesContainerRef}>
          {messages.length === 0 ? (
            <div className="chat-empty">
              <p>No hay mensajes aún</p>
              <p className="chat-empty-subtitle">Envía un mensaje para comenzar</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isSystemMessage = msg.senderId === 'system' || msg.senderRole === 'system';
              const messageClass = isSystemMessage 
                ? 'message message-system' 
                : `message ${msg.senderId === currentUser.uid ? 'message-sent' : 'message-received'}`;
              
              return (
                <div
                  key={msg.id}
                  className={messageClass}
                >
                  <div className="message-bubble">
                    {msg.fileURL && (
                      <div className="message-file">
                        {isImage(msg.fileType) ? (
                          <div className="message-image-container">
                            <ChatImage
                              src={msg.fileURL}
                              alt={msg.fileName}
                              storagePath={msg.storagePath}
                              className="message-image"
                              onClick={() => handleImageClick(msg.fileURL, msg.fileName, msg.storagePath)}
                            />
                            {canDeleteMessage(msg) && (
                              <button
                                onClick={() => handleDeleteMessage(msg.id, msg.fileURL)}
                                className="delete-image-btn"
                                disabled={deletingMessageId === msg.id}
                                title="Eliminar imagen"
                              >
                                {deletingMessageId === msg.id ? (
                                  <Loader size={16} className="spinner" />
                                ) : (
                                  <Trash2 size={16} />
                                )}
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleFileDownload(msg.fileURL, msg.fileName, msg.storagePath)}
                            className="message-file-link"
                          >
                            <span className="file-icon">{getFileIcon(msg.fileType)}</span>
                            <div className="file-info">
                              <span className="file-name" title={msg.fileName}>{truncateFileName(msg.fileName)}</span>
                              <span className="file-size">{formatFileSize(msg.fileSize)}</span>
                            </div>
                          </button>
                        )}
                      </div>
                    )}
                    {msg.content && (
                      <div className="message-text-container">
                        <p className="message-text">{msg.content}</p>
                        {translations[msg.id] && (
                          <p className="message-translation">{translations[msg.id]}</p>
                        )}
                        <div className="message-actions">
                          {isAdminUser && msg.senderId !== currentUser.uid && !isSystemMessage && (
                            <button
                              onClick={() => handleTranslateMessage(msg.id, msg.content)}
                              className="translate-msg-btn"
                              disabled={translatingId === msg.id}
                              title={translations[msg.id] ? "Hide translation" : "Translate"}
                            >
                              {translatingId === msg.id ? (
                                <Loader size={12} className="spinner" />
                              ) : (
                                <Languages size={12} />
                              )}
                              {translations[msg.id] ? "Hide" : "Translate"}
                            </button>
                          )}
                          {canDeleteMessage(msg) && !msg.fileURL && !isSystemMessage && (
                            <button
                              onClick={() => handleDeleteMessage(msg.id)}
                              className="delete-message-btn"
                              disabled={deletingMessageId === msg.id}
                              title="Eliminar mensaje"
                            >
                              {deletingMessageId === msg.id ? (
                                <Loader size={14} className="spinner" />
                              ) : (
                                <Trash2 size={14} />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {!isSystemMessage && (
                      <span className="message-time">{formatTimestamp(msg.createdAt)}</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chat-input-container">
          {selectedFile && (
            <div className="selected-file-preview">
              <span className="file-icon">{getFileIcon(selectedFile.type)}</span>
              <span className="file-name" title={selectedFile.name}>{truncateFileName(selectedFile.name)}</span>
              <button
                onClick={() => setSelectedFile(null)}
                className="remove-file-btn"
                disabled={uploading}
              >
                <X size={16} />
              </button>
            </div>
          )}
          
          {uploading && (
            <div className="upload-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <span>{Math.round(uploadProgress)}%</span>
            </div>
          )}

          <div className="chat-input">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            
            <button
              onClick={() => fileInputRef.current?.click()}
              className="attach-btn"
              disabled={uploading}
              title="Adjuntar archivo"
            >
              <Paperclip size={20} />
            </button>

            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe un mensaje..."
              disabled={uploading}
              className="message-input"
            />

            {isAdminUser && newMessage.trim() && (
              <button
                onClick={handleTranslateInput}
                disabled={translatingInput}
                className="translate-input-btn"
                title={`Translate to ${targetLang.toUpperCase()}`}
              >
                {translatingInput ? <Loader className="spinner" size={16} /> : <Languages size={16} />}
              </button>
            )}

            <button
              onClick={handleSendMessage}
              disabled={(!newMessage.trim() && !selectedFile) || uploading}
              className="send-btn"
              title="Enviar mensaje"
            >
              {uploading ? <Loader className="spinner" size={20} /> : <Send size={20} />}
            </button>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage.url}
          imageName={selectedImage.name}
          storagePath={selectedImage.storagePath}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
};

export default ChatWindow;
